import { apiClient } from "../../../shared/api/client";
import type { EarningStatus, PayoutStatus } from "../utils/agentEarnings";

// ─── Gains d'une Main ────────────────────────────────────────────────────────
// Ce que la Main a gagné, ce qu'elle peut retirer, et le détail course par
// course. Trois routes, toutes identifiées par le JETON : aucune ne prend
// d'identifiant d'agent, et il ne faut surtout pas en ajouter un.
//
// ⚠️ Hors du contrat embarqué (openapi v2.1.0) : arrivées avec la note du
// 2026-09-05. À revalider dès la régénération.

/**
 * Une ligne de gain — une course réglée.
 *
 * `frais_distance` et `retenue` ne sont pas décoratifs : le document demande de
 * MONTRER le calcul, parce qu'un gain qui s'explique se conteste moins. C'est
 * la seule raison pour laquelle le serveur les envoie en plus de `montant`.
 *
 * ⚠️ Type écrit À LA MAIN : l'endpoint n'est pas encore dans l'openapi embarqué
 * (v2.1.0). À remplacer par l'alias généré dès la régénération du contrat.
 */
export type AgentEarningLine = {
  trip_id:        string;
  /** Le gain net, déjà plafonné à zéro par le serveur. Fait foi. */
  montant:        number;
  /** Part distance des frais de service — la seule qui revienne à la Main. */
  frais_distance: number;
  /** Forfait fixe retenu par la plateforme. */
  retenue:        number;
  statut:         EarningStatus;
  date:           string;
};

/**
 * ⚠️ Type écrit À LA MAIN : l'endpoint n'est pas encore dans l'openapi embarqué
 * (v2.1.0). À remplacer par l'alias généré dès la régénération du contrat.
 */
export type AgentEarnings = {
  agent_id:           string;
  /**
   * **LE SEUL montant retirable, et il ne se recalcule pas.**
   *
   * Ce n'est PAS `total_gagne − deja_retire` : il déduit aussi ce qu'une demande
   * de retrait en cours retient déjà. Une Main qui lit un solde calculé par
   * l'écran puis se voit refuser « gains insuffisants » par le serveur perd
   * confiance dans les deux à la fois.
   */
  solde_disponible:   number;
  total_gagne:        number;
  deja_retire:        number;
  /** Frais de l'opérateur, à la charge de la Main. Vient du serveur. */
  frais_de_retrait:   number;
  /** Plancher d'une demande. Vient du serveur. */
  minimum_de_retrait: number;
  courses:            AgentEarningLine[];
};

/**
 * L'écran des gains — `GET /agent-earnings`.
 *
 * ## Aucun paramètre, et c'est voulu
 * La Main est identifiée par son jeton. Ne pas chercher à passer un `agent_id` :
 * même piège que `GET /trips?agent_id=`, silencieusement ignoré pour une Main
 * depuis le contrat du 2026-08-28.
 *
 * ## Ce que l'écran ne doit pas faire de cette réponse
 * `frais_de_retrait` et `minimum_de_retrait` **changent** — c'est précisément
 * pour ça qu'ils sont dans la charge utile plutôt que dans une constante. Les
 * coder en dur, c'est refuser un retrait légitime le jour où le plancher baisse.
 */
export async function getAgentEarnings(): Promise<AgentEarnings> {
  const { data } = await apiClient.get<AgentEarnings>("/agent-earnings");
  return data;
}

// ─── Retraits ────────────────────────────────────────────────────────────────
// La Main demande, un opérateur approuve, le prestataire vire. Trois étapes,
// donc trois occasions d'échouer — et aucune ne lui fait perdre ses gains.

export type RequestPayoutPayload = {
  amount: number;
  /**
   * **Obligatoire à la PREMIÈRE demande**, facultatif ensuite : le serveur
   * reprend alors le dernier numéro utilisé. L'écran le propose quand même,
   * pré-rempli et modifiable — une Main change de puce.
   */
  phone?: string;
};

/**
 * Une demande de retrait.
 *
 * ⚠️ Type écrit À LA MAIN : l'endpoint n'est pas encore dans l'openapi embarqué
 * (v2.1.0). À remplacer par l'alias généré dès la régénération du contrat.
 *
 * Les champs d'issue sont optionnels parce qu'ils dépendent de l'état : une
 * demande en attente n'a ni référence, ni motif, ni erreur.
 */
export type AgentPayout = {
  id:                string;
  /** Ce que la Main a demandé. */
  amount:            number;
  /**
   * Ce qu'elle recevra vraiment, frais déduits.
   *
   * ⚠️ C'est CE chiffre qu'on lui confirme après un `201`, jamais le montant
   * demandé : le calcul d'écran servait à annoncer, pas à faire foi.
   */
  net_amount:        number;
  phone?:            string;
  status:            PayoutStatus;
  /** Renseignée sur `PAYE` — la preuve du virement, à montrer. */
  payout_reference?: string | null;
  /** Motif d'un `REFUSE`, écrit par l'opérateur. À afficher tel quel. */
  resolution_note?:  string | null;
  /** Motif d'un `ECHOUE`, venu du prestataire. À afficher tel quel. */
  payout_error?:     string | null;
  created_at?:       string;
  updated_at?:       string;
};

/**
 * Demander un retrait — `POST /agent-payouts`.
 *
 * ## Les refus, et ce que l'écran en fait
 *  - 422 « Le retrait minimum est de N XOF. » → à BLOQUER AVANT l'envoi, avec
 *        `minimum_de_retrait` ; le voir signifie qu'on a laissé passer.
 *  - 422 « Gains insuffisants : N XOF disponibles. » → le solde a bougé depuis
 *        l'affichage : recharger `GET /agent-earnings`, ne pas se contenter du
 *        message.
 *  - 422 « Indiquez le numero qui doit recevoir l'argent. » → première demande
 *        sans numéro : ouvrir le champ.
 *
 * Dans les trois cas le message est écrit pour la Main : on l'affiche tel quel.
 */
export async function requestAgentPayout(payload: RequestPayoutPayload): Promise<AgentPayout> {
  const { data } = await apiClient.post<AgentPayout>("/agent-payouts", payload);
  return data;
}

/**
 * L'historique des retraits — `GET /agent-payouts/mine`.
 *
 * Sert aussi de source au numéro pré-rempli : le schéma `Agent` ne porte AUCUN
 * téléphone, et « le dernier numéro utilisé » n'existe nulle part ailleurs côté
 * app. Cf. `lastPayoutPhone`.
 *
 * ## Ce qu'il n'y a pas
 * Aucune annulation. Une fois envoyée, seul un opérateur tranche une demande —
 * ne pas offrir de bouton « annuler », le montrer sans qu'il fonctionne serait
 * pire que son absence.
 */
export async function listMyAgentPayouts(): Promise<AgentPayout[]> {
  const { data } = await apiClient.get<AgentPayout[]>("/agent-payouts/mine");
  return data;
}
